import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { Appearance, ColorSchemeName } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';

export type ThemeMode = 'light' | 'dark' | 'system';
export type ActiveTheme = 'light' | 'dark';

interface ThemeState {
  mode: ThemeMode;
  activeTheme: ActiveTheme;
  isInitialized: boolean;

  setMode: (mode: ThemeMode) => void;
  initialize: () => () => void;
}

const getActiveTheme = (
  mode: ThemeMode,
  systemTheme: ColorSchemeName | null | undefined
): ActiveTheme => {
  if (mode === 'system') {
    return systemTheme === 'dark' ? 'dark' : 'light';
  }
  return mode;
};

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      mode: 'system',
      activeTheme: Appearance.getColorScheme() === 'dark' ? 'dark' : 'light',
      isInitialized: false,

      setMode: (mode: ThemeMode) => {
        const systemTheme = Appearance.getColorScheme();
        set({
          mode,
          activeTheme: getActiveTheme(mode, systemTheme),
        });
      },

      initialize: () => {
        const { mode } = get();
        const systemTheme = Appearance.getColorScheme();

        // Set initial active theme based on current mode and system setting
        set({
          activeTheme: getActiveTheme(mode, systemTheme),
          isInitialized: true,
        });

        // Listen for system appearance changes
        const subscription = Appearance.addChangeListener(({ colorScheme }) => {
          const currentMode = get().mode;
          if (currentMode === 'system') {
            set({
              activeTheme: colorScheme === 'dark' ? 'dark' : 'light',
            });
          }
        });

        return () => {
          subscription.remove();
        };
      },
    }),
    {
      name: 'redi-theme-storage',
      storage: createJSONStorage(() => AsyncStorage),
      partialize: (state) => ({ mode: state.mode }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          const systemTheme = Appearance.getColorScheme();
          state.activeTheme = getActiveTheme(state.mode, systemTheme);
        }
      },
    }
  )
);
